import React, { useContext, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2, User, BookOpen } from 'lucide-react';
import axios from 'axios';
import { UserContext } from '@/lib/User';
import StudentIDCard from '@/components/course-dashboard/StudentIDCard';
import AccountDetails from '@/components/course-dashboard/AccountDetails';

const tabs = [
  { id: 'id-card', label: 'Student ID Card', icon: User },
  { id: 'account', label: 'Account Details', icon: BookOpen },
];

export default function Profile() {
  const { user } = useContext(UserContext);
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('id-card');

  const photoCacheKey = `xceedEdge_photo_${user?.email || user?.id || 'u'}`;

  useEffect(() => {
    if (!user?.session) return;
    axios
      .post('https://xceededge.com/get-user-details.php', { session: user.session }, { headers: { 'Content-Type': 'application/json' } })
      .then(res => {
        if (res.data.success) {
          const u = res.data.user || {};
          const enrolledCount = (res.data.enrolled_courses || []).length;
          const cachedPhoto = localStorage.getItem(photoCacheKey);
          setProfileData({
            full_name: u.full_name,
            email: u.email,
            phone_number: u.phone_number,
            profile_photo: u.profile_photo || cachedPhoto || null,
            courses_enrolled: enrolledCount,
          });
        }
      })
      .finally(() => setLoading(false));
  }, [user]);

  const handleProfileUpdate = (updates) => {
    setProfileData(prev => ({ ...prev, ...updates }));
    if (updates.profile_photo) {
      localStorage.setItem(photoCacheKey, updates.profile_photo);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">My Profile</h1>
        <p className="text-muted-foreground mt-1 text-sm">Your student identity and account settings</p>
      </div>

      <div className="flex gap-1 border-b border-border/60">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === tab.id
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        {activeTab === 'id-card' ? (
          <StudentIDCard course={profileData} user={user} />
        ) : (
          <AccountDetails course={profileData} user={user} onCourseUpdate={handleProfileUpdate} />
        )}
      </motion.div>
    </motion.div>
  );
}
